import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {hpcAttrs} from '@github-ui/hpc'
import {DisplayMode} from '@github-ui/issue-create/DisplayMode'
import {getSafeConfig} from '@github-ui/issue-create/getSafeConfig'
import {IssueCreateContextProvider} from '@github-ui/issue-create/IssueCreateContext'
import {TemplateListInternal, type TemplateListPaneFragment} from '@github-ui/issue-create/TemplateListPane'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useNavigate} from '@github-ui/use-navigate'
import {type User, useUser} from '@github-ui/use-user'
import {Heading, Link} from '@primer/react'

import {LABELS} from '../labels'
import type {RepositoryResult} from './issue-repo-new-choose-page-query-types'
import styles from './IssueCreatePane.module.css'

// RELAY-CLEANUP(IssueRepoNewChoosePageQuery): RelayCompat widens nullable fields so the still-Relay
// fragment data is assignable. Remove when IssueRepoNewChoosePageQuery leaves Relay.
export type IssueTemplateChooserRepository = RelayCompat<
  Pick<
    RepositoryResult,
    | 'id'
    | 'name'
    | 'nameWithOwner'
    | 'hasIssuesEnabled'
    | 'hasAnyTemplates'
    | 'issueForms'
    | 'issueTemplates'
    | 'isBlankIssuesEnabled'
    | 'isBlankIssuesMaintainerOverride'
    | 'isSecurityPolicyEnabled'
    | 'securityPolicyUrl'
    | 'contactLinks'
  >
> & {
  // The Relay fragment selects only `login`, so this stays narrower than the codegen owner type.
  readonly owner: Pick<RepositoryResult['owner'], 'login'>
}

/**
 * Renders the issue template chooser for a repository.
 *
 * Transport-agnostic: shared by the Relay page (`IssueRepoNewChoosePage`) and the Data Router
 * `issues` app route, which pass the same repository shape from different sources.
 *
 * The DataRouter app ships no app payload, so it passes `currentUser` from layout route data;
 * the Relay page omits it and falls back to `useUser`.
 */
export function IssueTemplateChooser(props: {repository: IssueTemplateChooserRepository; currentUser?: User | null}) {
  // Boundary wraps the inner component so a throw in IssueTemplateChooserInner's own render phase
  // (hooks, getSafeConfig, user handling) is still attributed to IssueNewChoosePage.
  return (
    <ErrorBoundary boundaryName="IssueNewChoosePage" critical>
      <IssueTemplateChooserInner {...props} />
    </ErrorBoundary>
  )
}

function IssueTemplateChooserInner({
  repository,
  currentUser: currentUserProp,
}: {
  repository: IssueTemplateChooserRepository
  currentUser?: User | null
}) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const {currentUser: appPayloadUser} = useUser()
  const currentUser = currentUserProp ?? appPayloadUser
  const navigate = useNavigate()

  const safeConfig = getSafeConfig({
    defaultDisplayMode: DisplayMode.TemplatePicker,
    insidePortal: false,
    canBypassTemplateSelection: true,
    navigate,
    issueCreateArguments: {
      repository: {
        owner: repository.owner.login,
        name: repository.name,
      },
    },
  })

  if (!currentUser) {
    return null
  }
  const {avatarUrl, login} = currentUser

  return (
    <IssueCreateContextProvider optionConfig={safeConfig} preselectedData={undefined}>
      <div className={styles.createPane}>
        <Link href={`/${login}`} className={styles.avatarLink} {...getHovercardAttributesForType('user', {login})}>
          <span className="sr-only">{LABELS.viewProfile(login)}</span>
          <GitHubAvatar src={avatarUrl} size={32} alt={''} className={styles.avatar} />
        </Link>
        <div className={styles.createPaneContainer} data-testid="issue-create-pane-container" {...hpcAttrs()}>
          <Heading as="h1" className={styles.header}>
            {LABELS.issueCreatePaneTitle}
          </Heading>
          <div className={styles.templateListContainer} tabIndex={-1}>
            {/* RELAY-CLEANUP(IssueRepoNewChoosePageQuery): narrows to the Repository fields
                TemplateListInternal needs. Remove when this query leaves Relay. */}
            <TemplateListInternal repository={repository as TemplateListPaneFragment} />
          </div>
        </div>
      </div>
    </IssueCreateContextProvider>
  )
}
